import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';
import { StringParam, useQueryParams } from 'use-query-params';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  createInquiry,
  loadInquiryTemplateData,
} from 'dashboard/redux/thunk-actions/inquiry-inspect';
import { loadWorkflowSchemaParallel } from 'dashboard/redux/thunk-actions/workflow-schema';
import DashboardInquiryInspectShowView from 'dashboard/views/DashboardInquiryInspectShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = {
  inquiryTemplateId: string;
  inquiryTemplateVersionId: string;
};

const DashboardInquiryInspectShowRoute = (props: PropsFromRedux) => {
  const { inquiryTemplateId, inquiryTemplateVersionId } = useParams<Params>() as Params;

  const [queryParams] = useQueryParams({
    'reference-id': StringParam,
  });

  const { loading: loadingInquiryTemplateData } = useApiLoader({
    loader: async () => {
      await Promise.all([
        props.loadInquiryTemplateData(inquiryTemplateVersionId, 'en-US'),
        props.loadWorkflowSchemaParallel(),
      ]);
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template for preview.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleCreateInspectInquiry, loading: createInspectInquiryLoading } =
    useApiCallback(
      async ({
        referenceId,
        accountTypeId,
        fields,
      }: {
        referenceId?: string;
        accountTypeId?: string;
        fields?: Record<string, any>;
      }) => {
        await props.createInquiry({
          inquiryTemplateId,
          inquiryTemplateVersionId,
          referenceId,
          accountTypeId,
          fields,
          envId: props.currentEnvironment.id,
        });
        props.showMessage('Created inquiry for inspect.', MessageType.Success);
      },
      [inquiryTemplateId, inquiryTemplateVersionId],
    );

  return (
    <DashboardInquiryInspectShowView
      inquiry={props.inquiry}
      inquiryTemplateVersion={props.inquiryTemplateVersion}
      inquiryTemplateVersionSchema={props.inquiryTemplateVersionSchema}
      inquiryTemplateVersionStepsPreview={props.inquiryTemplateVersionStepsPreview}
      inquiryTemplate={props.inquiryTemplate}
      referenceId={queryParams['reference-id']}
      workflowSchema={props.workflowSchema}
      loadingInquiry={createInspectInquiryLoading}
      loadingInquiryTemplateData={loadingInquiryTemplateData}
      handleCreateInquiry={handleCreateInspectInquiry}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  inquiry: state.inquiryInspect.inquiry,
  inquiryTemplate: state.inquiryInspect.inquiryTemplate,
  inquiryTemplateVersion: state.inquiryInspect.inquiryTemplateVersion,
  inquiryTemplateVersionSchema: state.inquiryInspect.inquiryTemplateVersionSchema,
  inquiryTemplateVersionStepsPreview: state.inquiryInspect.inquiryTemplateVersionStepsPreview,
  workflowSchema: state.workflowSchema,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
});

const mapDispatchToProps = {
  createInquiry,
  loadInquiryTemplateData,
  loadWorkflowSchemaParallel,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryInspectShowRoute);
